import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CategoryService from '@/services/CatalogServices/CategoryService';
import { extractError } from '@/helpers/extractApiError';

const model = {
    data: null,
    submitting: false,
    loading: false,
    errors: null,
    successOnCreate: '',
    categoryListOptions: [],
    categoryUnitTypes: [],
};

export const createCategory = createAsyncThunk(
    'category/createCategory',
    async (data, thunkApi) => {
        try {
            const { category_id: categoryId } = data;
            let response;
            if (categoryId) {
                response = await CategoryService.updateCategory(data, categoryId);
            } else {
                response = await CategoryService.createCategory(data);
            }
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getCategoryListOptions = createAsyncThunk(
    'category/getCategoryListOptions',
    async (data, thunkApi) => {
        try {
            const response = await CategoryService.getCategoryListOptions(data);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const viewCategory = createAsyncThunk(
    'category/viewCategory',
    async (categoryId, thunkApi) => {
        try {
            const response = await CategoryService.viewCategory(categoryId);
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

export const getCategoryUnitTypes = createAsyncThunk(
    'category/getCategoryUnitTypes',
    async (_, thunkApi) => {
        try {
            const response = await CategoryService.getCategoryUnitTypes();
            return response.data;
        } catch (error) {
            return thunkApi.rejectWithValue(extractError(error));
        }
    },
);

const createCategorySlice = createSlice({
    name: 'createCategory',
    initialState: model,
    reducers: {
        resetCategoryState: (state) => {
            state.data = null;
            state.submitting = false;
            state.errors = null;
        },
        resetCategoryStateSuccess: (state) => {
            state.successOnCreate = '';
        },
        resetCategoryStateError: (state) => {
            state.errors = null;
        },
    },
    extraReducers: (builder) => {
        // create category
        builder.addCase(createCategory.pending, (state) => {
            state.submitting = true;
        }).addCase(createCategory.fulfilled, (state, action) => {
            state.submitting = false;
            state.data = action.payload;
            state.successOnCreate = action.meta.arg.category_id ? 'Category updated successfully!' : 'Category created successfully!';
        }).addCase(createCategory.rejected, (state, action) => {
            state.submitting = false;
            state.errors = action.payload;
        });

        // get category list options
        builder.addCase(getCategoryListOptions.pending, (state) => {
            state.loading = true;
        }).addCase(getCategoryListOptions.fulfilled, (state, action) => {
            state.loading = false;
            state.categoryListOptions = action.payload;
        }).addCase(getCategoryListOptions.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // view category for edit
        builder.addCase(viewCategory.pending, (state) => {
            state.loading = true;
        }).addCase(viewCategory.fulfilled, (state, action) => {
            state.loading = false;
            state.data = action.payload;
        }).addCase(viewCategory.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });

        // get category unit types
        builder.addCase(getCategoryUnitTypes.pending, (state) => {
            state.loading = true;
        }).addCase(getCategoryUnitTypes.fulfilled, (state, action) => {
            state.loading = false;
            state.categoryUnitTypes = action.payload;
        }).addCase(getCategoryUnitTypes.rejected, (state, action) => {
            state.loading = false;
            state.errors = action.payload;
        });
    },
});

export const {
    resetCategoryState,
    resetCategoryStateSuccess,
    resetCategoryStateError,
} = createCategorySlice.actions;
export default createCategorySlice.reducer;
